import { TypedDocumentNode, gql } from '@apollo/client';
import { faShoppingCart } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  LoadingButton,
  Props as LoadingButtonProps,
} from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { NotMobileAppFeature } from '@sorare/core/src/components/BuyableFeature';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { ClickAddToCart_Origin } from '@sorare/core/src/protos/events/so5/shared/events';

import { useMarketplaceContext } from 'contexts/Marketplace';
import { useCartEvents } from 'hooks/events/useCartEvents';
import { useCart } from 'hooks/useCart';

import { AddToCart_anyCard } from './__generated__/index.graphql';

type Props = Omit<LoadingButtonProps, 'onClick' | 'loading'> & {
  anyCard: AddToCart_anyCard;
  origin: ClickAddToCart_Origin;
};

const StyledLoadingButton = styled(LoadingButton)`
  width: var(--quadruple-unit);
`;

const ConditionalTooltip = ({ children }: { children: ReactNode }) => (
  <Tooltip
    placement="top"
    title={
      <FormattedMessage
        id="addToCart.tooltip"
        defaultMessage="This item is already in your cart"
      />
    }
  >
    {children}
  </Tooltip>
);

export const AddToCart = ({
  anyCard,
  origin = ClickAddToCart_Origin.MARKETPLACE,
  children,
  ...rest
}: Props) => {
  const { trackClickAddToCart } = useCartEvents({ anyCard });
  const { addCardToCart, isInCart } = useCart();
  const { showAddToCartPopin } = useMarketplaceContext();

  const onClick = useLoggedCallback(() => {
    trackClickAddToCart(origin);
    addCardToCart.mutate(anyCard).then(cart => {
      showAddToCartPopin(
        cart?.mainCartItems?.find(ci => ci.card.slug === anyCard.slug)?.card
          .slug || null
      );
    });
  });

  const inCart = !!isInCart(anyCard);

  return (
    <NotMobileAppFeature>
      <ConditionalWrapper wrap={inCart} Wrapper={ConditionalTooltip}>
        <StyledLoadingButton
          loading={addCardToCart.loading}
          disabled={inCart}
          onClick={onClick}
          {...rest}
        >
          {children || <FontAwesomeIcon icon={faShoppingCart} size="sm" />}
        </StyledLoadingButton>
      </ConditionalWrapper>
    </NotMobileAppFeature>
  );
};

AddToCart.fragments = {
  anyCard: gql`
    fragment AddToCart_anyCard on AnyCardInterface {
      slug
      ...useCart_anyCard
      ...useCartEvents_anyCard
    }
    ${useCart.fragments.anyCard}
    ${useCartEvents.fragments.anyCard}
  ` as TypedDocumentNode<AddToCart_anyCard>,
};
